/**
 * Carrier MCP — OCS tool registrations (43 tools).
 *
 * All 43 OCS v1 methods are exposed as MCP tools. Each is wrapped with:
 *   - Scope enforcement (read / write / admin)
 *   - Dry-run short-circuit for destructive tools (no OCS call)
 *   - Sentry error capture
 *   - Audit hook into Analytics Engine
 *   - Per-user token resolution via getUserToken(sub)
 *
 * Scope assignments live in TOOL_SCOPES below; must match packages/ocs-spec/ocs-methods.json.
 *
 * PR #10 schema fixes applied:
 *   Fix #1:  getSimProviderStatus  — ICCID → simId lookup → bare integer
 *   Fix #2:  hlrGetBitrate         — ICCID → IMSI lookup → { imsi }
 *   Fix #3:  hlrSetBitrate         — ICCID → IMSI + bitrate → limit rename → { imsi, limit }
 *   Fix #4:  subscriberUsageOverPeriod       — { subscriber: { iccid }, period: { start, end } }
 *   Fix #5:  subscriberNetworkEventsOverPeriod — same nested shape
 *   Fix #6:  modifySubscriberStatus          — { subscriber, newStatus }
 *   Fix #7:  modifySubscriberBalance         — { subscriber, amount } or { subscriber, setBalance }
 *   Fix #8:  changeSimStatus                 — ICCID → simId + simStatus → newStatus
 *   Fix #9:  modifySubscriberContactInfo     — firstName+lastName → name, email→mail, phone→phone
 *   Fix #10: setSubscriberTrafficRestrictions — typed booleans, not JSON string
 *   Fix #11: sendMtSms                       — ICCID→IMSI, message→text, sender→senderId
 *   Fix #12: listSponsor                     — bare integer (resellerId)
 *   Fix #13: listSteeringList                — bare integer (resellerId)
 *   Fix #14: getCustomerTariff               — bare integer + response key listTariffRule
 *   Fix #15: listDetailedLocationZone        — bare integer (resellerId)
 *   Fix #16: modifySubscriberSteeringList    — { subscriber, steeringListId }
 */

import { applySubscriberFilters, buildListSubscriberParams, esimStatusPerAccountParams, imsiFromSubscriberRecord, locationParams, locationZoneCreateParams, networkEventsOverPeriodParams, ocsHlrBitrate, ocsHlrBitrateFromBps, ocsLocalDateTime, ocsSimStatus, packageTemplateCreateFromToolJson, packageTemplateModifyFromToolJson, prepaidPackageLimits, recurringIdForPackage, recurringPackageParams, subscriberIdParams, subscriberSimId, usageOverPeriodParams, withOcsListSummary, type OcsListKind } from "@carrier/ocs-client";
import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import * as Sentry from "@sentry/cloudflare";
import { OcsClient, OcsApiError } from "./client.js";
import type { Env, CarrierProps, AuditRow, ToolScope } from "./types.js";
import type { ManagedAuth } from "./ocs-scoping.js";
import { checkCallQuota, recordUsage, TIER_CALL_LIMITS, UPGRADE_URL } from "./billing.js";
import {
  boundTariffResult,
  boundZonesResult,
  TARIFF_MAX_RULES,
  TARIFF_VERBOSE_MAX_RULES,
  ZONES_MAX_OPERATORS,
  type TrafficType,
} from "./bounded-results.js";

export interface ToolContext {
  env: Env;
  props: CarrierProps;
  /** Managed-mode scoping context (empty for BYO / stdio). */
  managedAuth: ManagedAuth;
  audit: (row: AuditRow) => void;
  getUserToken: (sub: string) => Promise<string>;
}

// Scope metadata per tool — all 43 tools declared here.
// Must stay in sync with ocs-methods.json from packages/ocs-spec.
export const TOOL_SCOPES: Record<string, ToolScope> = {
  // --- read ---
  list_reseller_accounts: "read",
  get_reseller_info: "read",
  esim_status_per_account: "read",
  list_sponsors: "read",
  list_steering_lists: "read",
  get_subscriber: "read",
  list_subscribers: "read",
  get_sim_provider_status: "read",
  get_subscriber_location: "read",
  hlr_get_bitrate: "read",
  list_subscriber_packages: "read",
  list_package_templates: "read",
  list_location_zones: "read",
  list_detailed_location_zones: "read",
  list_destination_prefixes: "read",
  subscriber_usage: "read",
  subscriber_network_events: "read",
  subscriber_active_period: "read",
  get_tariff: "read",
  list_network_profiles: "read",
  // --- intelligence composites ---
  detect_country_entry: "read",
  // --- write ---
  modify_subscriber_balance: "write",
  modify_subscriber_status: "write",
  modify_subscriber_contact_info: "write",
  set_subscriber_traffic_restrictions: "write",
  modify_subscriber_steering_list: "write",
  move_subscriber_range_to_account: "write",
  hlr_set_bitrate: "write",
  assign_package: "write",
  assign_recurring_package: "write",
  modify_package_limits: "write",
  modify_package_expiry: "write",
  modify_package_status: "write",
  stop_resume_recurring_package: "write",
  create_package_template: "write",
  create_location_zone: "write",
  // --- admin ---
  modify_account_balance: "admin", // corrected per ocs-spec PR #6
  change_sim_status: "admin",
  // Removing ONE package from ONE subscriber is a write, not an admin
  // action. Admin-gating it made package lifecycle write-once for any
  // operator on a read/write token: they could assign a package but had no
  // way to remove one, including one assigned in error. clean_all_packages
  // stays admin — wiping every package is a different blast radius.
  delete_subscriber_package: "write",
  clean_all_packages: "admin",
  modify_template_core: "admin",
  modify_template_recurring: "admin",
  modify_template_throttling: "admin",
  send_sms: "admin",
};

export const DESTRUCTIVE_TOOLS = new Set([
  "modify_account_balance",
  "modify_subscriber_balance",
  "modify_subscriber_status",
  "change_sim_status",
  "modify_subscriber_contact_info",
  "set_subscriber_traffic_restrictions",
  "modify_subscriber_steering_list",
  "move_subscriber_range_to_account",
  "hlr_set_bitrate",
  "assign_package",
  "assign_recurring_package",
  "modify_package_limits",
  "modify_package_expiry",
  "modify_package_status",
  "stop_resume_recurring_package",
  "delete_subscriber_package",
  "clean_all_packages",
  "modify_template_core",
  "modify_template_recurring",
  "modify_template_throttling",
  "create_package_template",
  "create_location_zone",
  "send_sms",
  // v1.1 backlog — same dry_run contract as v1 write/admin tools (tools-backlog.ts)
  "affect_subscriber_phone_number",
  "modify_subscriber_mobile_plan",
  "modify_subscriber_package_active_period",
  "modify_subscriber_voip_plan",
  "push_steering_to_subscriber",
  "reset_subscriber_gz_counter",
  // S6 live-smoke gap fill (CAR-105)
  "change_network_profile_of_location_zone",
]);

type ToolResult = {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
};

/**
 * Wraps a tool handler with scope enforcement, dry-run short-circuit,
 * Sentry capture, and audit logging.
 */
export function wrapHandler<T extends Record<string, unknown>>(
  toolName: string,
  ocsMethod: string,
  requiredScope: ToolScope,
  ctx: ToolContext,
  handler: (args: T, token: string) => Promise<ToolResult>,
) {
  return async (args: T & { dry_run?: boolean }): Promise<ToolResult> => {
    const start = Date.now();
    const isDryRun = args.dry_run === true;

    // Scope enforcement
    if (!ctx.props.scope.includes(requiredScope)) {
      ctx.audit({
        tool_name: toolName,
        ocs_method: ocsMethod,
        status: "scope_denied",
        dry_run: false,
        duration_ms: 0,
      });
      return {
        isError: true,
        content: [
          {
            type: "text",
            text: `Scope denied: tool '${toolName}' requires '${requiredScope}' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
          },
        ],
      };
    }

    // Billing quota check (Phase 7 — v1.1)
    // Enterprise is unlimited; free/pro are gate-checked against KV counter.
    const quota = await checkCallQuota(ctx.env, ctx.props.sub, ctx.props.tier);
    if (!quota.allowed) {
      ctx.audit({
        tool_name: toolName,
        ocs_method: ocsMethod,
        status: "quota_exceeded",
        dry_run: isDryRun,
        duration_ms: 0,
      });
      return {
        isError: true,
        content: [
          {
            type: "text",
            text: [
              `Quota exceeded: your ${quota.tier} plan has reached the monthly limit of ${TIER_CALL_LIMITS.free.toLocaleString()} regular tool calls.`,
              `Resets ${quota.resetAt}.`,
              `Upgrade to Pro for unlimited calls at ${UPGRADE_URL}`,
            ].join(" "),
          },
        ],
      };
    }

    // Dry-run short-circuit for destructive tools
    if (isDryRun && DESTRUCTIVE_TOOLS.has(toolName)) {
      ctx.audit({
        tool_name: toolName,
        ocs_method: ocsMethod,
        status: "dry_run",
        dry_run: true,
        duration_ms: 0,
      });
      return {
        content: [
          {
            type: "text",
            text: `[dry_run=true] Would execute '${toolName}' (OCS method '${ocsMethod}') with args: ${JSON.stringify(args)}. No changes made.`,
          },
        ],
      };
    }

    let result: ToolResult;
    try {
      const token = await ctx.getUserToken(ctx.props.sub);
      result = await handler(args, token);
    } catch (err) {
      try {
        Sentry.captureException(err, {
          tags: {
            tool: toolName,
            feature: "mcp",
            reseller_id: String(ctx.props.reseller_id),
          },
        });
      } catch {
        // Sentry may not be initialised in tests — swallow.
      }
      const message = err instanceof Error ? err.message : String(err);
      const ocsCode = err instanceof OcsApiError ? err.code : undefined;
      ctx.audit({
        tool_name: toolName,
        ocs_method: ocsMethod,
        status: "error",
        dry_run: isDryRun,
        duration_ms: Date.now() - start,
        ...(ocsCode !== undefined ? { ocs_status_code: ocsCode } : {}),
      });
      return {
        isError: true,
        content: [{ type: "text", text: `Error: ${message}` }],
      };
    }

    ctx.audit({
      tool_name: toolName,
      ocs_method: ocsMethod,
      status: result.isError ? "error" : "ok",
      dry_run: isDryRun,
      duration_ms: Date.now() - start,
    });

    // Record usage on successful (non-error, non-dry-run) calls.
    // Fire-and-forget — never blocks the response.
    if (!result.isError && !isDryRun) {
      recordUsage(ctx.env, ctx.props.sub, ctx.props.tier);
    }

    return result;
  };
}

// ---------------------------------------------------------------------------
// OCS call helper — accepts explicit token (no module-level singleton).
// Fix: params broadened to accept bare scalar for methods that expect it
// (listSponsor, listSteeringList, getCustomerTariff, listDetailedLocationZone,
//  getSimProviderStatus).
// ---------------------------------------------------------------------------
async function ocsCall<T = Record<string, unknown>>(
  env: Env,
  token: string,
  method: string,
  params: Record<string, unknown> | number | string = {},
  /**
   * List envelope kind. Pass it for any method whose result is a list and the
   * server counts the rows for the caller — see ocs-summary.ts in
   * @carrier/ocs-client for why a model must not be asked to tally a JSON
   * array. Kept in step with apps/mcp-server/src/tools.ts: this fork is what
   * `npx @carrierllc/mcp` runs, so a fix applied only there reaches nobody who
   * installs the CLI.
   */
  listKind?: OcsListKind,
): Promise<ToolResult> {
  const client = new OcsClient(env.CARRIER_OCS_BASE_URL, token);
  const result = await client.call<T>(method, params);
  const payload = listKind ? withOcsListSummary(result, listKind) : result;
  return {
    content: [{ type: "text", text: JSON.stringify(payload, null, 2) }],
  };
}

// ---------------------------------------------------------------------------
// Request-scoped subscriber lookup cache (ICCID → subscriber record).
// Prevents N+1 lookups when tools need simId or IMSI from the same subscriber.
// Fix #1, #2, #3, #8, #11 depend on this resolver.
// ---------------------------------------------------------------------------
type SubscriberRecord = Record<string, unknown>;

export async function resolveSubscriberByIccid(
  env: Env,
  token: string,
  iccid: string,
  cache: Map<string, SubscriberRecord>,
): Promise<SubscriberRecord> {
  const hit = cache.get(iccid);
  if (hit) return hit;
  const client = new OcsClient(env.CARRIER_OCS_BASE_URL, token);
  const record = await client.call<SubscriberRecord>("getSingleSubscriber", { iccid });
  cache.set(iccid, record);
  return record;
}

// ---------------------------------------------------------------------------
// Fetch the token owner's reseller ID via getResellerInfo.
// Used for bare-integer OCS methods: listSponsor, listSteeringList,
// getCustomerTariff, listDetailedLocationZone.
// Fix #12, #13, #14, #15.
// ---------------------------------------------------------------------------
export async function getDefaultResellerId(env: Env, token: string): Promise<number> {
  const client = new OcsClient(env.CARRIER_OCS_BASE_URL, token);
  const info = await client.call<{ id?: number }>("getResellerInfo", {});
  const id = info?.id;
  if (typeof id !== "number") {
    throw new Error("Could not determine resellerId from getResellerInfo");
  }
  return id;
}

// Shorthand: build the destructive-tool annotation + dry_run schema field
const DRY_RUN_FIELD = {
  dry_run: z
    .boolean()
    .optional()
    .describe(
      "If true, do not call OCS — return the would-be request for confirmation",
    ),
};

export function registerAllTools(server: McpServer, ctx: ToolContext): void {
  // =========================================================================
  // 1. RESELLER TOOLS
  // =========================================================================

  server.registerTool(
    "list_reseller_accounts",
    {
      title: "List Reseller Accounts",
      description:
        "Use this to enumerate the accounts (sub-resellers or customer accounts) BELOW a reseller. " +
        "Params: `resellerId` (integer, optional — omit to list accounts under the token owner's reseller). " +
        "Returns: `{ summary, reseller: [ { id, name, account: [ { id, name, balance, packageOnly } ] } ] }` — the " +
        "accounts are NESTED under each reseller, not a flat top-level array. `summary.total` therefore " +
        "counts RESELLERS; the account count is `summary.nested.accounts`. For any question about " +
        "accounts use that, never `total`. " +
        "Do NOT use this to fetch a single subscriber's details — use `get_subscriber` instead. " +
        "Do NOT use this to check eSIM activation counts — use `esim_status_per_account` for that. " +
        "Do NOT use this to find a reseller's PARENT, balance or charging plans — those are above or on " +
        "the reseller itself, not in this list; use `get_reseller_info`.",
      inputSchema: {
        resellerId: z
          .number()
          .optional()
          .describe("Filter to a specific reseller by ID (omit for token owner's reseller)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_reseller_accounts",
      "listResellerAccount",
      TOOL_SCOPES["list_reseller_accounts"]!,
      ctx,
      async ({ resellerId }, token) => {
        const params: Record<string, unknown> = {};
        if (resellerId !== undefined) params.resellerId = resellerId;
        return ocsCall(ctx.env, token, "listResellerAccount", params, "resellers");
      },
    ),
  );

  server.registerTool(
    "modify_account_balance",
    {
      title: "Modify Account Balance",
      description:
        "Use this to adjust or set the monetary balance on a reseller account. " +
        "'adapt' mode adds (positive amount) or subtracts (negative amount) from the current balance; " +
        "'set' mode replaces the balance with the exact amount. Every change is logged as a transaction. " +
        "Params: `accountId` (integer account ID from `list_reseller_accounts`), `amount` (number), " +
        "`mode` ('adapt' | 'set'). " +
        "Returns: updated account balance record with the transaction ID. " +
        "Do NOT use this to modify a subscriber's personal balance — use `modify_subscriber_balance` instead. " +
        "Always call `list_reseller_accounts` first to confirm the target accountId before executing.",
      inputSchema: {
        accountId: z.number().describe("The account ID to modify"),
        amount: z.number().describe("Amount to add (adapt) or set to (set)"),
        mode: z
          .enum(["adapt", "set"])
          .describe("'adapt' adds/subtracts, 'set' replaces the balance"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_account_balance",
      "modifyAccountBalance",
      TOOL_SCOPES["modify_account_balance"]!,
      ctx,
      async ({ accountId, amount, mode }, token) => {
        // Sam's decision (2026-08-28), fixing the last money-moving entry in
        // KNOWN_BROKEN. There is no `mode` property on live OCS, so every
        // call from the old handler was rejected outright on an unknown
        // field. Bridge4IP's own documented contract for this method
        // (docs/research/bridge4ip-ocs-api/endpoints-full.md §1.2) needs no
        // direction classifier at all — `amount` is a signed delta (negative
        // to subtract), same pattern as the sibling `modifySubscriberBalance`.
        // An earlier version of this fix added a `type: 'credit'|'debit'`
        // field because a param-enumeration probe listed `type` as a known
        // property on the OCS model; live-tested 2026-08-28 with type absent,
        // present with "credit"/"debit", and present with a garbage string —
        // all four produced an identical result, so `type` is not load-bearing
        // and was dropped in favour of the documented, sibling-consistent
        // shape.
        const params: Record<string, unknown> = { accountId };
        if (mode === "set") {
          params.setBalance = amount;
        } else {
          params.amount = amount;
        }
        return ocsCall(ctx.env, token, "modifyAccountBalance", params);
      },
    ),
  );

  server.registerTool(
    "get_reseller_info",
    {
      title: "Get Reseller Info",
      description:
        "Use this to retrieve full details for a reseller: identity and PARENT in the reseller " +
        "hierarchy, balance, traffic configuration, charging plans, and contact info. " +
        "This is the tool that answers who sits ABOVE a reseller, and which mobile or VoIP " +
        "charging plan it is on. " +
        "Params: `resellerId` (integer, optional — omit to return the token owner's reseller). " +
        "Returns: `id`, `name`, `parentId` and `parentName` (the reseller directly above this one), " +
        "`balance` (a decimal STRING, e.g. \"46.31\"), `cdrFolder`, " +
        "`trafficInfo` (relayGy / relayCallSms / relayLU / relayVoIP booleans), " +
        "`chargingInfo.mobilePlan` and `chargingInfo.voipPlan` (each `{id, name}`), and `contactInfo`. " +
        "Do NOT use this to list all accounts under a reseller — use `list_reseller_accounts` for that. " +
        "Do NOT use `list_reseller_accounts` to find a reseller's parent or charging plans: it returns " +
        "the accounts BELOW a reseller and carries neither.",
      inputSchema: {
        resellerId: z
          .number()
          .optional()
          .describe("Reseller ID (omit for token owner)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "get_reseller_info",
      "getResellerInfo",
      TOOL_SCOPES["get_reseller_info"]!,
      ctx,
      async ({ resellerId }, token) => {
        const params: Record<string, unknown> = {};
        if (resellerId !== undefined) params.resellerId = resellerId;
        return ocsCall(ctx.env, token, "getResellerInfo", params);
      },
    ),
  );

  server.registerTool(
    "esim_status_per_account",
    {
      title: "eSIM Status Per Account",
      description:
        "Use this to get eSIM status counts broken down by account: active, suspended, inventory " +
        "(not yet activated), and other states. Good for fleet health dashboards and capacity planning. " +
        "OCS requires either `accountId` OR `resellerId`. If both omitted, the token owner's reseller is resolved automatically. " +
        "Params: `accountId` (integer, optional — for a single account), `resellerId` (integer, optional — for all accounts under a specific reseller). " +
        "Returns: array of per-account objects with `accountId`, `active`, `suspended`, `inventory`, `other`. " +
        "Do NOT use this to check a single subscriber's status — use `get_subscriber` for that. " +
        "Do NOT use this for billing or balance checks — use `list_reseller_accounts` for balances.",
      inputSchema: {
        accountId: z
          .number()
          .optional()
          .describe("Filter to a specific account"),
        resellerId: z
          .number()
          .optional()
          .describe("Reseller ID (omit to use the token owner's reseller)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "esim_status_per_account",
      "esimStatusPerAccount",
      TOOL_SCOPES["esim_status_per_account"]!,
      ctx,
      async ({ accountId, resellerId }, token) => {
        const params = esimStatusPerAccountParams(
          accountId !== undefined
            ? { accountId }
            : { resellerId: resellerId ?? (await getDefaultResellerId(ctx.env, token)) },
        );
        return ocsCall(ctx.env, token, "esimStatusPerAccount", params);
      },
    ),
  );

  // Fix #12: OCS expects bare integer (resellerId), not {}
  server.registerTool(
    "list_sponsors",
    {
      title: "List Sponsors",
      description:
        "Use this to list all sponsor networks (eSIM sponsor carriers) available to this reseller. " +
        "A sponsor defines which physical network infrastructure backs a given eSIM profile. " +
        "Params: `resellerId` (integer, optional — omit to use the token owner's reseller). " +
        "Returns: array of sponsor records with `sponsorId`, `name`, and coverage metadata. " +
        "Do NOT use this to list steering lists or network profiles — those are separate concepts. " +
        "Use `list_steering_lists` to see operator preference configurations.",
      inputSchema: {
        resellerId: z
          .number()
          .optional()
          .describe("Reseller ID (omit to use token owner's reseller)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_sponsors",
      "listSponsor",
      TOOL_SCOPES["list_sponsors"]!,
      ctx,
      async ({ resellerId }, token) => {
        const id = resellerId ?? (await getDefaultResellerId(ctx.env, token));
        return ocsCall(ctx.env, token, "listSponsor", id);
      },
    ),
  );

  // Fix #13: OCS expects bare integer (resellerId), not {}
  server.registerTool(
    "list_steering_lists",
    {
      title: "List Steering Lists",
      description:
        "Use this to retrieve all network steering lists configured for this reseller. " +
        "A steering list is a named configuration of excluded and priority mobile operators that " +
        "controls which networks an eSIM prefers to roam onto — the primary mechanism for network " +
        "quality optimisation and cost control. Call this before `modify_subscriber_steering_list` " +
        "to obtain valid steering list IDs. " +
        "Params: `resellerId` (integer, optional — omit to use the token owner's reseller). " +
        "Returns: array of steering list records with `steeringListId`, `name`, and configured operators. " +
        "Do NOT use this to assign a steering list to a subscriber — use `modify_subscriber_steering_list`. " +
        "Do NOT use this to push a steering change to a device — use `push_steering_to_subscriber` after assignment.",
      inputSchema: {
        resellerId: z
          .number()
          .optional()
          .describe("Reseller ID (omit to use token owner's reseller)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_steering_lists",
      "listSteeringList",
      TOOL_SCOPES["list_steering_lists"]!,
      ctx,
      async ({ resellerId }, token) => {
        const id = resellerId ?? (await getDefaultResellerId(ctx.env, token));
        return ocsCall(ctx.env, token, "listSteeringList", id);
      },
    ),
  );

  // =========================================================================
  // 2. SUBSCRIBER TOOLS
  // =========================================================================

  server.registerTool(
    "get_subscriber",
    {
      title: "Get Single Subscriber",
      description:
        "Use this as the primary lookup for a single subscriber by ICCID or MSISDN. " +
        "Returns the complete subscriber record: status, balance, assigned account, contact info, " +
        "IMSI, simId, steering list, active pricing plan, and traffic restriction flags. " +
        "Params: `iccid` (20-digit ICC identifier, optional) OR `msisdn` (E.164 phone number, optional) — " +
        "provide at least one. " +
        "Optional: `with_gz_counter` (boolean) — when true, includes `greenZoneCounter` in the response: " +
        "{ subscriberId, volumeOnGZ (bytes consumed on reseller whitelist hosts/IPs after bundle depletion), " +
        "lastResetDate, lastUpdateDate }. Omit or set false to skip the GZ counter (default). " +
        "Returns: full subscriber object. Key fields: `status` (ACTIVE/SUSPENDED/TERMINATED), " +
        "`balance`, `imsi`, `simId`, `steeringListId`. " +
        "Do NOT use this for bulk lookups — use `list_subscribers` with filters for that.",
      inputSchema: {
        iccid: z.string().optional().describe("The ICCID of the subscriber"),
        msisdn: z
          .string()
          .optional()
          .describe("The MSISDN (phone number) of the subscriber"),
        with_gz_counter: z
          .boolean()
          .optional()
          .describe(
            "When true, include greenZoneCounter { subscriberId, volumeOnGZ (bytes), lastResetDate, lastUpdateDate } — tracks bytes consumed on reseller whitelist hosts/IPs after bundle depletion",
          ),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "get_subscriber",
      "getSingleSubscriber",
      TOOL_SCOPES["get_subscriber"]!,
      ctx,
      async ({ iccid, msisdn, with_gz_counter }, token) => {
        const params: Record<string, unknown> = {};
        if (iccid) params.iccid = iccid;
        if (msisdn) params.msisdn = msisdn;
        if (with_gz_counter === true) params.withGzCounter = true;
        return ocsCall(ctx.env, token, "getSingleSubscriber", params);
      },
    ),
  );

  server.registerTool(
    "list_subscribers",
    {
      title: "List Subscribers",
      description:
        "Use this to list subscribers with optional filters and pagination. Good for fleet enumeration, " +
        "bulk status checks, and finding subscribers by account or status. " +
        "OCS REQUIRES at least one search key — provide exactly one of: `imsi`, `iccid`, `activationCode`, " +
        "`accountId`, or `msisdn`. Calling with no key will be rejected before reaching OCS. " +
        "Params: `imsi` (string), `iccid` (string), `activationCode` (string), `accountId` (integer), " +
        "`msisdn` (string), `status` (string, e.g. 'ACTIVE'/'SUSPENDED'), `offset` (integer, pagination — default 0), " +
        "`limit` (integer, max results — always set to avoid unbounded fetches; recommended max 100 per call). " +
        "Returns: `{ summary, subscriberList: [...] }` — subscriber summary records with ICCID, status " +
        "and account, behind a server-counted `summary.total` (rows returned) and `summary.breakdown.status`. " +
        "Answer 'how many' from `summary`, not by counting rows — but when `summary.moreAvailable` is set " +
        "this is one page, so report `summary.upstreamTotal` for the fleet-wide figure, never `total`. " +
        "Do NOT use this to fetch full details for a specific subscriber — use `get_subscriber` for that.",
      inputSchema: z
        .object({
          imsi: z.string().optional().describe("Filter by IMSI"),
          iccid: z.string().optional().describe("Filter by ICCID"),
          activationCode: z
            .string()
            .optional()
            .describe("Filter by activation code"),
          accountId: z.number().optional().describe("Filter by account ID"),
          msisdn: z.string().optional().describe("Filter by MSISDN"),
          status: z.string().optional().describe("Filter by status"),
          offset: z.number().optional().describe("Pagination offset"),
          limit: z.number().optional().describe("Max results to return"),
        })
        .refine(
          (d) =>
            d.imsi !== undefined ||
            d.iccid !== undefined ||
            d.activationCode !== undefined ||
            d.accountId !== undefined ||
            d.msisdn !== undefined,
          {
            message:
              "Provide at least one of: imsi, iccid, activationCode, accountId, msisdn",
          },
        ),
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_subscribers",
      "listSubscriber",
      TOOL_SCOPES["list_subscribers"]!,
      ctx,
      async (args, token) => {
        // OCS listSubscriber does not accept `limit` — strip it before forwarding.
        // We keep `limit` in the inputSchema as a UX hint so callers can express intent.
        // OCS listSubscriber accepts EXACTLY five properties:
        //   accountId, activationCode, imsiPrefix, iccidPrefix, msisdnPrefix
        // Anything else fails the WHOLE request, e.g.
        //   "Unrecognized field status ... 5 known properties: accountId,
        //    activationCode, imsiPrefix, iccidPrefix, msisdnPrefix"
        //
        // This copy previously sent `imsi`/`iccid`/`msisdn` plus `status` and
        // `offset`, so it diverged from the Worker on the same tool call: a
        // status filter the Worker quietly ignored would error outright here.
        // `status`, `offset` and `limit` are applied to the returned rows
        // instead, matching the Worker exactly.
        //
        // Note the *Prefix names: OCS matches these as PREFIXES. A full ICCID
        // still matches (it is a prefix of itself).
        const params = buildListSubscriberParams(args);
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const raw = await client.call<unknown>("listSubscriber", params);
        const payload = applySubscriberFilters(raw, args);

        // Counted after the filters, so `summary.total` is the number of rows
        // actually returned rather than the number OCS held before paging.
        return {
          content: [
            {
              type: "text",
              text: JSON.stringify(withOcsListSummary(payload, "subscribers"), null, 2),
            },
          ],
        };
      },
    ),
  );

  // Fix #7: OCS expects { subscriber, amount } or { subscriber, setBalance } not { iccid, adaptBalance|setBalance }
  server.registerTool(
    "modify_subscriber_balance",
    {
      title: "Modify Subscriber Balance",
      description:
        "Use this to adjust or set the monetary balance for an individual subscriber. " +
        "'adapt' mode adds (positive) or subtracts (negative) from the current balance; " +
        "'set' mode replaces the balance with the exact amount provided. " +
        "Params: `iccid` (subscriber identifier), `amount` (number), `mode` ('adapt' | 'set'). " +
        "Returns: updated subscriber balance. " +
        "Do NOT use this to modify an account-level balance — use `modify_account_balance` for that. " +
        "Always call `get_subscriber` first to capture the current balance before adjusting.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        amount: z.number().describe("Amount to add (adapt) or set to (set)"),
        mode: z
          .enum(["adapt", "set"])
          .describe("'adapt' adds/subtracts, 'set' replaces"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_subscriber_balance",
      "modifySubscriberBalance",
      TOOL_SCOPES["modify_subscriber_balance"]!,
      ctx,
      async ({ iccid, amount, mode }, token) => {
        const params: Record<string, unknown> = { subscriber: iccid };
        if (mode === "adapt") params.amount = amount;
        else params.setBalance = amount;
        return ocsCall(ctx.env, token, "modifySubscriberBalance", params);
      },
    ),
  );

  // Fix #6: OCS expects { subscriber, newStatus } not { iccid, status }
  server.registerTool(
    "modify_subscriber_status",
    {
      title: "Modify Subscriber Status",
      description:
        "Use this to change the OCS lifecycle status of a subscriber. " +
        "Common transitions: ACTIVE → SUSPENDED (pause without losing packages), " +
        "SUSPENDED → ACTIVE (reactivate), ACTIVE/SUSPENDED → TERMINATED (irreversible). " +
        "WARNING: TERMINATED status is permanent — the subscriber record cannot be reactivated. " +
        "⚠ Setting status to END_OF_LIFE is irreversible. The subscriber becomes read-only (OCS error 17 on subsequent mutations). Confirm before calling. " +
        "Params: `iccid` (subscriber identifier), `status` (new status string, e.g. 'ACTIVE', " +
        "'SUSPENDED', 'TERMINATED', 'END_OF_LIFE'). " +
        "Returns: updated subscriber record with the new status. " +
        "Do NOT use this to disable the SIM card at the network level — use `change_sim_status` for that. " +
        "Always call `get_subscriber` first to confirm current status before modifying.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        status: z.string().describe("New status value"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_subscriber_status",
      "modifySubscriberStatus",
      TOOL_SCOPES["modify_subscriber_status"]!,
      ctx,
      async ({ iccid, status }, token) =>
        ocsCall(ctx.env, token, "modifySubscriberStatus", {
          subscriber: iccid,
          newStatus: status,
        }),
    ),
  );

  // Fix #8: OCS expects { simId, newStatus } not { iccid, simStatus }.
  // Resolve ICCID → simId via getSingleSubscriber, then send { simId, newStatus }.
  server.registerTool(
    "change_sim_status",
    {
      title: "Change SIM Status",
      description:
        "Use this to change the physical SIM/eSIM card status at the SIM provider level, " +
        "independent of the OCS subscriber lifecycle status. " +
        "Statuses: ENABLED maps to OCS Affected (normal operation) and DISABLED maps to OCS Free (blocked at the network). " +
        "DELETED is not a changeSimStatus value. Always use `dry_run=true` first. " +
        "Internally resolves ICCID → numeric simId via a getSingleSubscriber call before forwarding to OCS. " +
        "Params: `iccid` (subscriber identifier), `simStatus` ('ENABLED' | 'DISABLED'). " +
        "Returns: updated SIM record with new status. " +
        "Do NOT use this to change the subscriber's OCS lifecycle status — use `modify_subscriber_status`. " +
        "Do NOT send DELETED.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        simStatus: z.string().describe("New SIM status: ENABLED (OCS Affected) or DISABLED (OCS Free)"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "change_sim_status",
      "changeSimStatus",
      TOOL_SCOPES["change_sim_status"]!,
      ctx,
      async ({ iccid, simStatus }, token) => {
        const newStatus = ocsSimStatus(simStatus);
        if (typeof newStatus !== "string") {
          return {
            isError: true,
            content: [{ type: "text", text: `Error: ${newStatus.error}` }],
          };
        }
        const cache = new Map<string, SubscriberRecord>();
        const sub = await resolveSubscriberByIccid(ctx.env, token, iccid, cache);
        const simId = subscriberSimId(sub);
        if (simId === undefined) {
          return {
            isError: true,
            content: [{ type: "text", text: `Error: Could not resolve simId for ICCID ${iccid}` }],
          };
        }
        return ocsCall(ctx.env, token, "changeSimStatus", {
          simId,
          newStatus,
        });
      },
    ),
  );

  // Fix #1: OCS expects bare Long (simId integer) not { iccid }.
  // Resolve ICCID → simId via getSingleSubscriber, then send bare integer.
  server.registerTool(
    "get_sim_provider_status",
    {
      title: "Get SIM Provider Status",
      description:
        "Use this to check the physical SIM/eSIM card status at the SIM provider level " +
        "(ENABLED, DISABLED, DELETED) — distinct from the OCS subscriber status. " +
        "Useful when `get_subscriber` shows ACTIVE but connectivity is broken; the SIM may be " +
        "DISABLED at the provider level. Internally resolves ICCID → numeric simId. " +
        "Params: `iccid` (subscriber identifier). " +
        "Returns: provider status object with `simStatus`, `activationDate`, `lastStatusChange`. " +
        "Do NOT use this to change the SIM status — use `change_sim_status` for that.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "get_sim_provider_status",
      "getSimProviderStatus",
      TOOL_SCOPES["get_sim_provider_status"]!,
      ctx,
      async ({ iccid }, token) => {
        const cache = new Map<string, SubscriberRecord>();
        const sub = await resolveSubscriberByIccid(ctx.env, token, iccid, cache);
        const simId = subscriberSimId(sub);
        if (simId === undefined) {
          return {
            isError: true,
            content: [{ type: "text", text: `Error: Could not resolve simId for ICCID ${iccid}` }],
          };
        }
        return ocsCall(ctx.env, token, "getSimProviderStatus", simId);
      },
    ),
  );

  server.registerTool(
    "get_subscriber_location",
    {
      title: "Get Subscriber Location",
      description:
        "Returns last-known location from subscriber's most recent cell tower usage. Complementary to GeoSense get_subscriber_location_by_cell_id — use this when you have the subscriber id, use the cell-id variant when you have raw cell parameters.",
      inputSchema: { iccid: z.string().describe("The subscriber ICCID") },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "get_subscriber_location",
      "getSubscriberLocation",
      TOOL_SCOPES["get_subscriber_location"]!,
      ctx,
      async ({ iccid }, token) =>
        ocsCall(ctx.env, token, "getSubscriberLocation", locationParams(iccid)),
    ),
  );

  // Fix #9: OCS expects { subscriber, name, company, phone, mail }
  // not { iccid, firstName, lastName, email, phoneNumber }
  server.registerTool(
    "modify_subscriber_contact_info",
    {
      title: "Modify Subscriber Contact Info",
      description:
        "Use this to update the contact details stored on a subscriber record in OCS. " +
        "Only the fields you provide are updated — omitted fields are left unchanged. " +
        "Params: `iccid` (subscriber identifier), `firstName` (optional), `lastName` (optional), " +
        "`company` (optional), `email` (optional), `phoneNumber` (optional). " +
        "Returns: updated subscriber contact record. " +
        "Do NOT use this to change subscriber status, balance, or traffic flags — those have dedicated tools.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        firstName: z.string().optional().describe("First name"),
        lastName: z.string().optional().describe("Last name"),
        company: z.string().optional().describe("Company name"),
        email: z.string().optional().describe("Email address"),
        phoneNumber: z.string().optional().describe("Phone number"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_subscriber_contact_info",
      "modifySubscriberContactInfo",
      TOOL_SCOPES["modify_subscriber_contact_info"]!,
      ctx,
      async ({ iccid, firstName, lastName, company, email, phoneNumber }, token) => {
        const params: Record<string, unknown> = { subscriber: iccid };
        const nameParts = [firstName, lastName].filter(Boolean);
        if (nameParts.length > 0) params.name = nameParts.join(" ");
        if (company !== undefined) params.company = company;
        if (phoneNumber !== undefined) params.phone = phoneNumber;
        if (email !== undefined) params.mail = email;
        return ocsCall(ctx.env, token, "modifySubscriberContactInfo", params);
      },
    ),
  );

  // Fix #10: OCS expects { subscriber, mtcAllowed, smsMoAllowed, dataAllowed, mocAllowed }
  // Drop JSON-string antipattern; use typed booleans directly.
  server.registerTool(
    "set_subscriber_traffic_restrictions",
    {
      title: "Set Traffic Restrictions",
      description:
        "Use this to enable or disable individual traffic types for a subscriber: mobile data, " +
        "voice calls (mobile-originated and mobile-terminated), and SMS. Omit any flag to leave " +
        "it unchanged. Changes take effect immediately at the OCS level. " +
        "Params: `iccid` (subscriber identifier), `dataAllowed` (boolean, controls data traffic), " +
        "`mocAllowed` (boolean, controls outbound calls), `mtcAllowed` (boolean, controls inbound calls), " +
        "`smsMoAllowed` (boolean, controls outbound SMS). " +
        "Returns: updated traffic restriction record for the subscriber. " +
        "Do NOT use this to throttle bandwidth — use `hlr_set_bitrate` for speed limiting. " +
        "Do NOT use this to suspend the subscriber entirely — use `modify_subscriber_status` (SUSPENDED) instead.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        mtcAllowed: z.boolean().optional().describe("Allow mobile-terminated calls"),
        smsMoAllowed: z.boolean().optional().describe("Allow SMS mobile-originated"),
        dataAllowed: z.boolean().optional().describe("Allow data traffic"),
        mocAllowed: z.boolean().optional().describe("Allow mobile-originated calls"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "set_subscriber_traffic_restrictions",
      "setSubscriberTrafficRestrictions",
      TOOL_SCOPES["set_subscriber_traffic_restrictions"]!,
      ctx,
      async ({ iccid, mtcAllowed, smsMoAllowed, dataAllowed, mocAllowed }, token) => {
        const params: Record<string, unknown> = { subscriber: iccid };
        if (mtcAllowed !== undefined) params.mtcAllowed = mtcAllowed;
        if (smsMoAllowed !== undefined) params.smsMoAllowed = smsMoAllowed;
        if (dataAllowed !== undefined) params.dataAllowed = dataAllowed;
        if (mocAllowed !== undefined) params.mocAllowed = mocAllowed;
        return ocsCall(ctx.env, token, "setSubscriberTrafficRestrictions", params);
      },
    ),
  );

  // Fix #16: OCS expects { subscriber, steeringListId } not { iccid, steeringListId }
  server.registerTool(
    "modify_subscriber_steering_list",
    {
      title: "Modify Subscriber Steering List",
      description:
        "Use this to assign or remove a network steering list on a specific subscriber, controlling " +
        "which mobile operators the subscriber's eSIM prefers to connect to. Steering lists are " +
        "managed separately — call `list_steering_lists` to get valid IDs. " +
        "This operates at the SUBSCRIBER level only. After assigning, call `push_steering_to_subscriber` " +
        "to push the change to the physical device immediately; without that call the device continues " +
        "using the old operator preference list until next re-registration. " +
        "Params: `iccid` (subscriber identifier), `steeringListId` (integer from `list_steering_lists`, " +
        "or null/0 to remove the current steering list). " +
        "Returns: updated subscriber record confirming the new steeringListId. " +
        "Do NOT use this for account-level steering (no MCP tool yet — gap G-03, awaiting eSIMVault input).",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        steeringListId: z.number().describe("The steering list ID to assign"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_subscriber_steering_list",
      "modifySubscriberSteeringList",
      TOOL_SCOPES["modify_subscriber_steering_list"]!,
      ctx,
      async ({ iccid, steeringListId }, token) =>
        ocsCall(ctx.env, token, "modifySubscriberSteeringList", {
          subscriber: iccid,
          steeringListId,
        }),
    ),
  );

  server.registerTool(
    "move_subscriber_range_to_account",
    {
      title: "Move Subscribers to Account",
      description:
        "BREAKING CHANGE (2026-08-28): this tool's contract was rebuilt around account-ID " +
        "ranges to match what OCS actually accepts — ICCID-pair targeting (`iccidFrom`/`iccidTo` " +
        "+ a single `accountId`) is gone. Zero field names were ever shared with OCS's real " +
        "`moveSubscriberRangeToAccount` shape (verified live 2026-08-24 — see " +
        "packages/ocs-spec/ocs-accepted-params.json), so every prior call to this tool failed " +
        "outright; nothing that used to work stops working. " +
        "Use this to move a contiguous range of subscribers, identified by IMSI or ICCID, from " +
        "one account to another. Cross-reseller moves lose the subscribers' packages. " +
        "Params: `srcAccountId` (source account ID from `list_reseller_accounts`), " +
        "`destAccount` (destination account ID), `rangeType` ('IMSI' | 'ICCID' — which identifier " +
        "`rangeStart`/`rangeEnd` are expressed in), `rangeStart` (inclusive start of the range), " +
        "`rangeEnd` (inclusive end of the range). " +
        "Returns: OCS confirmation with the count of subscribers moved. " +
        "Do NOT use this for a single subscriber move — provide identical rangeStart and rangeEnd. " +
        "Always call `list_subscribers` on the range first to verify the correct subscribers are included.",
      inputSchema: {
        srcAccountId: z.number().describe("Source account ID to move subscribers FROM"),
        destAccount: z.number().describe("Destination account ID to move subscribers TO"),
        rangeType: z
          .enum(["IMSI", "ICCID"])
          .describe("Whether rangeStart/rangeEnd identify subscribers by IMSI or ICCID"),
        rangeStart: z.string().describe("Inclusive start of the identifier range"),
        rangeEnd: z.string().describe("Inclusive end of the identifier range"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "move_subscriber_range_to_account",
      "moveSubscriberRangeToAccount",
      TOOL_SCOPES["move_subscriber_range_to_account"]!,
      ctx,
      async ({ srcAccountId, destAccount, rangeType, rangeStart, rangeEnd }, token) =>
        ocsCall(ctx.env, token, "moveSubscriberRangeToAccount", {
          srcAccountId,
          destAccount,
          rangeType,
          rangeStart,
          rangeEnd,
        }),
    ),
  );

  // Fix #3: OCS expects { imsi, limit } not { iccid, bitrate }.
  // Resolve ICCID → IMSI via getSingleSubscriber; rename bitrate → limit.
  // B2.4: added bitrate_string string-enum alternative; refine ensures at least one of (bitrate, bitrate_string).
  server.registerTool(
    "hlr_set_bitrate",
    {
      title: "Set HLR Bitrate",
      description:
        "Use this to set a hard bandwidth cap for a subscriber at the HLR (Home Location Register) level. " +
        "This is a network-level throttle applied regardless of package allowance — use it to enforce " +
        "fair-use speed limits or to throttle heavy users without suspending service. " +
        "Provide at least one of `bitrate` (bits per second) or `bitrate_string` (OCS string enum); " +
        "when both are provided, `bitrate_string` is used. " +
        "The wire value is only the string enum: KB_32, KB_64, KB_128, KB_256, KB_384, KB_512, KB_1024, KB_2048, KB_3072, " +
        "KB_5120, KB_7680, KB_10240, KB_20480, KB_51200, KB_102400, UNLIMITED. " +
        "A bits-per-second number is mapped onto that enum (256000 is KB_256, 1024000 is KB_1024). 0 is UNLIMITED. " +
        "Any other number is refused before OCS. " +
        "Internally resolves ICCID → IMSI via a getSingleSubscriber lookup. " +
        "Params: `iccid` (subscriber identifier), `bitrate` (integer bits per second) OR " +
        "`bitrate_string` (string enum from the list above). " +
        "Returns: HLR confirmation with the applied bitrate. " +
        "Do NOT use this to block data entirely — use `set_subscriber_traffic_restrictions` with `dataAllowed=false`. " +
        "Do NOT use this to change throttling thresholds on a package template — use `modify_template_throttling`.",
      inputSchema: z
        .object({
          iccid: z.string().describe("The subscriber ICCID"),
          bitrate: z.number().optional().describe("Max bitrate in bits per second. 256000 is KB_256. 0 is UNLIMITED."),
          bitrate_string: z
            .enum([
              "KB_32", "KB_64", "KB_128", "KB_256", "KB_384", "KB_512",
              "KB_1024", "KB_2048", "KB_3072", "KB_5120", "KB_7680",
              "KB_10240", "KB_20480", "KB_51200", "KB_102400", "UNLIMITED",
            ])
            .optional()
            .describe("Max bitrate as OCS string enum (alternative to numeric `bitrate`)"),
          dry_run: z
            .boolean()
            .optional()
            .describe("If true, do not call OCS — return the would-be request for confirmation"),
        })
        .refine(
          (d) => d.bitrate !== undefined || d.bitrate_string !== undefined,
          { message: "Provide at least one of `bitrate` (number) or `bitrate_string` (string enum)" },
        ),
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "hlr_set_bitrate",
      "hlrSetBitrate",
      TOOL_SCOPES["hlr_set_bitrate"]!,
      ctx,
      async ({ iccid, bitrate, bitrate_string }, token) => {
        const limit = bitrate_string !== undefined ? ocsHlrBitrate(bitrate_string) : ocsHlrBitrateFromBps(bitrate ?? Number.NaN);
        if (typeof limit !== "string") {
          return {
            isError: true,
            content: [{ type: "text", text: `Error: ${limit.error}` }],
          };
        }
        const cache = new Map<string, SubscriberRecord>();
        const sub = await resolveSubscriberByIccid(ctx.env, token, iccid, cache);
        const imsi = imsiFromSubscriberRecord(sub);
        if (typeof imsi !== "string" || imsi.length === 0) {
          return {
            isError: true,
            content: [{ type: "text", text: `Error: Could not resolve IMSI for ICCID ${iccid}` }],
          };
        }
        return ocsCall(ctx.env, token, "hlrSetBitrate", { imsi, limit });
      },
    ),
  );

  // Fix #2: OCS expects { imsi } not { iccid }.
  // Resolve ICCID → IMSI via getSingleSubscriber.
  server.registerTool(
    "hlr_get_bitrate",
    {
      title: "Get HLR Bitrate",
      description:
        "Use this to read the current HLR-level bandwidth cap applied to a subscriber. " +
        "A non-zero value means the subscriber is throttled to that speed regardless of package allowance. " +
        "A zero or null response means no HLR-level cap is in effect. " +
        "Internally resolves ICCID → IMSI via a getSingleSubscriber lookup. " +
        "Params: `iccid` (subscriber identifier). " +
        "Returns: object with `bitrate` (integer, bits-per-second) or null if no limit is set. " +
        "Do NOT use this to check package data allowance limits — use `list_subscriber_packages` for that.",
      inputSchema: { iccid: z.string().describe("The subscriber ICCID") },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "hlr_get_bitrate",
      "hlrGetBitrate",
      TOOL_SCOPES["hlr_get_bitrate"]!,
      ctx,
      async ({ iccid }, token) => {
        const cache = new Map<string, SubscriberRecord>();
        const sub = await resolveSubscriberByIccid(ctx.env, token, iccid, cache);
        const imsi = imsiFromSubscriberRecord(sub);
        if (typeof imsi !== "string" || imsi.length === 0) {
          return {
            isError: true,
            content: [{ type: "text", text: `Error: Could not resolve IMSI for ICCID ${iccid}` }],
          };
        }
        return ocsCall(ctx.env, token, "hlrGetBitrate", { imsi });
      },
    ),
  );

  // =========================================================================
  // 3. PACKAGE TOOLS
  // =========================================================================

  server.registerTool(
    "list_subscriber_packages",
    {
      title: "List Subscriber Packages",
      description:
        "Use this to retrieve all prepaid packages currently assigned to a subscriber. " +
        "Returns each package's allowance (data/voice/SMS), consumed usage, expiry date, status, and packageId. " +
        "Always call this before any package modification tool (`modify_package_limits`, " +
        "`modify_package_expiry`, `modify_package_status`, `delete_subscriber_package`) to confirm " +
        "the correct packageId and current state. " +
        "Params: `iccid` (subscriber identifier). " +
        "Returns: `{ summary, packages: [...] }` — package records with `packageId`, `name`, " +
        "`status`, `dataLimit`, `dataUsed`, `expirationDate` and a `recurring` flag, behind a " +
        "server-counted `summary.total` and a `summary.breakdown` over status and recurring. " +
        "Answer 'how many' from `summary`, not by counting rows. " +
        "Do NOT use this to browse the product catalog — use `list_package_templates` for that.",
      inputSchema: { iccid: z.string().describe("The subscriber ICCID") },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_subscriber_packages",
      "listSubscriberPrepaidPackages",
      TOOL_SCOPES["list_subscriber_packages"]!,
      ctx,
      async ({ iccid }, token) =>
        ocsCall(ctx.env, token, "listSubscriberPrepaidPackages", { iccid }, "packages"),
    ),
  );

  server.registerTool(
    "assign_package",
    {
      title: "Assign Package to Subscriber",
      description:
        "Use this to assign a one-time prepaid data/voice package to a subscriber from an existing template. " +
        "The package is active immediately (or at first usage, depending on template settings). " +
        "Params: `iccid` (subscriber identifier), `packageTemplateId` (integer from `list_package_templates`), " +
        "`account_for_subs` (integer, optional — when provided instead of resolving a subscriber, OCS auto-selects " +
        "a free eSIM from that account and assigns the package; this is the bulk auto-provisioning path). " +
        "Returns: created package record with `packageId`, `startDate`, `endDate`, and allowances. " +
        "Do NOT use this for packages that should auto-renew — use `assign_recurring_package` instead. " +
        "Do NOT use this to provision a new subscriber end-to-end — consider `provision_esim_wizard` " +
        "for a guided flow with dry-run preview.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        packageTemplateId: z
          .number()
          .describe("The package template ID to assign"),
        account_for_subs: z
          .number()
          .optional()
          .describe(
            "Account ID — when provided, OCS auto-selects a free eSIM from this account for bulk provisioning",
          ),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "assign_package",
      "affectPackageToSubscriber",
      TOOL_SCOPES["assign_package"]!,
      ctx,
      async ({ iccid, packageTemplateId, account_for_subs }, token) => {
        // accountForSubs is an alternative to subscriber per OCS — never send both.
        if (account_for_subs !== undefined) {
          return ocsCall(ctx.env, token, "affectPackageToSubscriber", {
            packageTemplateId,
            accountForSubs: account_for_subs,
          });
        }
        // OCS affectPackageToSubscriber wants a SubscriberId object {iccid}, not a bare integer.
        return ocsCall(ctx.env, token, "affectPackageToSubscriber", {
          subscriber: { iccid },
          packageTemplateId,
        });
      },
    ),
  );

  server.registerTool(
    "assign_recurring_package",
    {
      title: "Assign Recurring Package",
      description:
        "Use this to assign an auto-renewing prepaid package to a subscriber. The package renews " +
        "automatically based on the template's periodicity settings, reducing churn from manual renewal. " +
        "Params: `iccid` (subscriber identifier), `packageTemplateId` (integer from `list_package_templates` " +
        "— must be a template configured with recurring/periodicity settings), " +
        "`activation_at_first_use` (boolean, optional — when true, package activates on the subscriber's " +
        "first network usage rather than immediately; mutually exclusive with `start_time_utc`), " +
        "`start_time_utc` (ISO 8601 UTC datetime, optional — schedules a specific activation start; " +
        "mutually exclusive with `activation_at_first_use`). " +
        "Returns: created recurring package record with `packageId` and renewal schedule. " +
        "Do NOT use this for one-time packages — use `assign_package` instead. " +
        "To pause or cancel auto-renewal without deleting the package, use `stop_resume_recurring_package`.",
      inputSchema: z
        .object({
          iccid: z.string().describe("The subscriber ICCID"),
          packageTemplateId: z.number().describe("The package template ID"),
          activation_at_first_use: z
            .boolean()
            .optional()
            .describe(
              "When true, package activates on first network usage (mutually exclusive with start_time_utc)",
            ),
          start_time_utc: z
            .string()
            .optional()
            .describe(
              "Scheduled activation datetime in ISO 8601 UTC format (mutually exclusive with activation_at_first_use)",
            ),
          dry_run: z
            .boolean()
            .optional()
            .describe("If true, do not call OCS — return the would-be request for confirmation"),
        })
        .refine(
          (d) => !(d.activation_at_first_use === true && d.start_time_utc !== undefined),
          {
            message:
              "activation_at_first_use and start_time_utc are mutually exclusive — choose one",
          },
        ),
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "assign_recurring_package",
      "affectRecurringPackageToSubscriber",
      TOOL_SCOPES["assign_recurring_package"]!,
      ctx,
      async ({ iccid, packageTemplateId, activation_at_first_use, start_time_utc }, token) => {
        const params = recurringPackageParams(iccid, packageTemplateId, {
          activationAtFirstUse: activation_at_first_use,
          startTimeUTC: start_time_utc,
        });
        return ocsCall(ctx.env, token, "affectRecurringPackageToSubscriber", params);
      },
    ),
  );

  server.registerTool(
    "modify_package_limits",
    {
      title: "Modify Package Limits",
      description:
        "Use this to change the data, voice, or SMS allowance ceilings on an already-assigned subscriber package. " +
        "Useful for mid-cycle top-ups or corrections without assigning a new package. " +
        "Params: `iccid` (subscriber identifier), `packageId` (integer from `list_subscriber_packages`), " +
        "`limits` (JSON string with the limit fields to change, e.g. {\"dataLimit\": 5368709120}). " +
        "Returns: updated package record with new limits. " +
        "Do NOT use this to change the package template (affecting future subscribers) — use `modify_template_core`. " +
        "Do NOT use this to change expiry — use `modify_package_expiry`. " +
        "Always call `list_subscriber_packages` first to confirm the correct packageId.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        packageId: z.number().describe("The active package ID"),
        limits: z.string().describe("New limits as JSON string"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_package_limits",
      "modifySubscriberPrepaidPackageLimits",
      TOOL_SCOPES["modify_package_limits"]!,
      ctx,
      async ({ packageId, limits }, token) =>
        // OCS accepts only packageId/newLimits/comment here, so `iccid` never
        // reaches the wire, and the limits nest under `newLimits`
        // (dataByte/mocSecond/mtcSecond/moSms/mtSms) rather than sitting flat.
        // mcp-stdio is a BYO-token surface, so unlike mcp-server there is no
        // ownership-evidence use for the ICCID here.
        ocsCall(ctx.env, token, "modifySubscriberPrepaidPackageLimits", {
          packageId,
          newLimits: prepaidPackageLimits(JSON.parse(limits) as Record<string, unknown>),
        }),
    ),
  );

  server.registerTool(
    "modify_package_expiry",
    {
      title: "Modify Package Expiry Date",
      description:
        "Use this to extend or shorten the expiry date of an active prepaid package on a subscriber. " +
        "Useful when a subscriber's trip is longer than expected or for promotional extensions. " +
        "Params: `iccid` (subscriber identifier), `packageId` (integer from `list_subscriber_packages`), " +
        "`expirationDate` (ISO 8601 date string, e.g. '2026-06-01' or '2026-06-01T23:59:59' — absolute date), " +
        "`validity_days` (integer, optional — number of days from now; passed to OCS as `newValidityDuration`; " +
        "provide either `expirationDate` OR `validity_days`, not both). " +
        "Returns: updated package record with the new expiry date. " +
        "Do NOT use this to change when a package becomes active — use `modify_subscriber_package_active_period`. " +
        "Do NOT use this to change data allowances — use `modify_package_limits`.",
      inputSchema: z
        .object({
          iccid: z.string().describe("The subscriber ICCID"),
          packageId: z.number().describe("The active package ID"),
          expirationDate: z
            .string()
            .optional()
            .describe("New expiry date (ISO 8601 format, absolute)"),
          validity_days: z
            .number()
            .int()
            .positive()
            .optional()
            .describe("Number of days from now until expiry (alternative to expirationDate)"),
          ...DRY_RUN_FIELD,
        })
        .refine(
          (d) => d.expirationDate !== undefined || d.validity_days !== undefined,
          {
            message:
              "Provide at least one of `expirationDate` (absolute) or `validity_days` (relative)",
          },
        )
        .refine(
          (d) => !(d.expirationDate !== undefined && d.validity_days !== undefined),
          {
            message:
              "`expirationDate` and `validity_days` are mutually exclusive — choose one",
          },
        ),
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_package_expiry",
      "modifySubscriberPrepaidPackageExpDate",
      TOOL_SCOPES["modify_package_expiry"]!,
      ctx,
      async ({ packageId, expirationDate, validity_days }, token) => {
        // `iccid` is not an OCS field here, and `expirationDate` is not the
        // OCS name. The documented absolute input is `newExpirationDate`
        // (endpoints-full.md §3.5). `newDateUtc`/`newPeriod` are enumerated by
        // the probe but undocumented and unused by any sibling, so they are
        // not sent — same call as the vestigial `type` on modifyAccountBalance.
        const params: Record<string, unknown> = { packageId };
        if (expirationDate !== undefined) {
          params.newExpirationDate = ocsLocalDateTime(expirationDate);
        }
        if (validity_days !== undefined) params.newValidityDuration = validity_days;
        return ocsCall(ctx.env, token, "modifySubscriberPrepaidPackageExpDate", params);
      },
    ),
  );

  server.registerTool(
    "modify_package_status",
    {
      title: "Modify Package Status",
      description:
        "Use this to activate or deactivate a specific prepaid package on a subscriber without " +
        "removing it. A deactivated package retains its allowances and can be reactivated later. " +
        "Params: `iccid` (subscriber identifier), `packageId` (integer from `list_subscriber_packages`), " +
        "`status` (new package status string, e.g. 'ACTIVE', 'INACTIVE'). " +
        "Returns: updated package record with the new status. " +
        "Do NOT use this to delete a package — use `delete_subscriber_package` for permanent removal. " +
        "Do NOT use this to change the subscriber's overall account status — use `modify_subscriber_status`.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        packageId: z.number().describe("The active package ID"),
        status: z.string().describe("New package status"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_package_status",
      "modifySubscriberPrepaidPackageStatus",
      TOOL_SCOPES["modify_package_status"]!,
      ctx,
      async ({ packageId, status }, token) => {
        // OCS modifySubscriberPrepaidPackageStatus accepts exactly two
        // properties: `subsPrepaidPackageId` and `active` (a boolean).
        // Sending `iccid` fails the whole request:
        //   "Unrecognized field iccid ... 2 known properties:
        //    subsPrepaidPackageId, active"
        // and there is no `status` string field at all. The tool's public
        // params are unchanged — `iccid` still identifies the subscriber in
        // the schema for the caller, it just never reaches OCS — and
        // `status` is mapped to the boolean OCS actually wants. mcp-stdio is
        // a BYO-token surface (no shared master token), so unlike
        // mcp-server's fix there is no ownership-evidence use for `iccid`
        // here. Ported from apps/mcp-server/src/tools.ts, which already had
        // this fix — the two surfaces had drifted (#808 param-drift.mjs).
        const normalised = String(status).trim().toUpperCase();
        if (normalised !== "ACTIVE" && normalised !== "INACTIVE") {
          return {
            isError: true,
            content: [
              {
                type: "text" as const,
                text: `Invalid status "${status}". Use "ACTIVE" or "INACTIVE".`,
              },
            ],
          };
        }
        return ocsCall(ctx.env, token, "modifySubscriberPrepaidPackageStatus", {
          subsPrepaidPackageId: packageId,
          active: normalised === "ACTIVE",
        });
      },
    ),
  );

  server.registerTool(
    "stop_resume_recurring_package",
    {
      title: "Stop/Resume Recurring Package",
      description:
        "Use this to pause or restart the auto-renewal cycle of a recurring package without removing it. " +
        "'stop' halts future renewals (subscriber keeps current period until expiry); " +
        "'resume' re-enables auto-renewal from the next renewal date. " +
        "Params: `iccid` (subscriber identifier), `packageId` (integer from `list_subscriber_packages`), " +
        "`action` ('stop' | 'resume'). " +
        "Returns: updated recurring package record with new renewal state. " +
        "Do NOT use this to permanently delete a recurring package — use `delete_subscriber_package`. " +
        "Do NOT confuse this with `modify_package_status` (which activates/deactivates a package for usage).",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        packageId: z.number().describe("The recurring package ID"),
        action: z
          .enum(["stop", "resume"])
          .describe("Whether to stop or resume"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "stop_resume_recurring_package",
      "stopResumeSubsRecurringPackage",
      TOOL_SCOPES["stop_resume_recurring_package"]!,
      ctx,
      async ({ iccid, packageId, action }, token) => {
        // Three different id spaces, which is why no rename could fix this.
        // OCS wants `recurringId` — the recurring SUBSCRIPTION — and the tool
        // holds `subscriberprepaidpackageid`, one package that subscription
        // produced. `templateId`, the third option OCS offers, would stop the
        // template for every subscriber on it. Resolve the real recurring id
        // from the subscriber's package list, and refuse if it is not there:
        // guessing here changes an unrelated subscription.
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);
        const listed = await client.call<Record<string, unknown>>(
          "listSubscriberPrepaidPackages",
          { iccid },
        );
        const recurringId = recurringIdForPackage(listed, packageId);
        if (recurringId === undefined) {
          return {
            isError: true,
            content: [
              {
                type: "text" as const,
                text:
                  `Package ${packageId} is not a recurring package on ICCID ${iccid}. ` +
                  `stop_resume_recurring_package needs a package that came from a ` +
                  `recurring template — call list_subscriber_packages and pick one ` +
                  `that reports a recurring subscription.`,
              },
            ],
          };
        }
        return ocsCall(ctx.env, token, "stopResumeSubsRecurringPackage", {
          recurringId,
          active: action === "resume",
        });
      },
    ),
  );

  server.registerTool(
    "delete_subscriber_package",
    {
      title: "Delete Subscriber Package",
      description:
        "Use this to permanently remove a single prepaid package from a subscriber. " +
        "This is irreversible — the package record and any unused allowance are deleted. " +
        "Always call `list_subscriber_packages` first to confirm the correct packageId and snapshot " +
        "the current state. Use `dry_run=true` on the first call. " +
        "Params: `iccid` (subscriber identifier), `packageId` (integer from `list_subscriber_packages`). " +
        "Returns: OCS confirmation of deletion. " +
        "Do NOT use this to remove ALL packages at once — use `clean_all_packages` for that (requires separate confirm). " +
        "Do NOT use this to pause a package — use `modify_package_status` to deactivate it instead.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID (for audit logging; OCS itself does not take it — see below)"),
        packageId: z.number().describe("The package ID to delete (OCS's sole parameter — a bare integer)"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "delete_subscriber_package",
      "deleteSubscriberPackage",
      TOOL_SCOPES["delete_subscriber_package"]!,
      ctx,
      // OCS deleteSubscriberPackage takes a BARE INTEGER package id
      // (endpoints-full.md 3.8, "Numeric value = package ID") — not an
      // { iccid, packageId } object. iccid is kept in the tool's input schema
      // for audit/description purposes only; it is not an OCS parameter.
      async ({ packageId }, token) =>
        ocsCall(ctx.env, token, "deleteSubscriberPackage", packageId),
    ),
  );

  server.registerTool(
    "clean_all_packages",
    {
      title: "Clean All Subscriber Packages",
      description:
        "DANGEROUS: Removes ALL prepaid packages from a subscriber in a single irreversible operation. " +
        "There is no undo. Typical use: resetting a subscriber to zero before re-provisioning a new package series. " +
        "REQUIRED workflow: (1) call `list_subscriber_packages` to snapshot what will be deleted; " +
        "(2) call this tool with `dry_run=true` to preview; (3) get explicit user confirmation; " +
        "(4) call again with `dry_run=false`. " +
        "Params: `iccid` (subscriber identifier), `dry_run` (boolean — MUST be true on first call). " +
        "Returns: list of packages that were (or would be) deleted. " +
        "Do NOT use this to remove a single package — use `delete_subscriber_package` instead.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "clean_all_packages",
      "cleanSubscriberAllPackages",
      TOOL_SCOPES["clean_all_packages"]!,
      ctx,
      async ({ iccid }, token) =>
        ocsCall(ctx.env, token, "cleanSubscriberAllPackages", { iccid }),
    ),
  );

  // =========================================================================
  // 4. PACKAGE TEMPLATE TOOLS
  // =========================================================================

  server.registerTool(
    "list_package_templates",
    {
      title: "List Package Templates",
      description:
        "Use this to browse the product catalog of prepaid package templates available for assignment. " +
        "Returns each template's name, data/voice/SMS limits, pricing, validity period, location zone, " +
        "and recurring configuration. Call this before `assign_package` or `assign_recurring_package` " +
        "to obtain valid `packageTemplateId` values. " +
        "Params: none — returns the whole catalog for the token owner's reseller. " +
        "Returns: `{ summary, template: [...] }`. `summary.total` is the number of templates, counted by " +
        "the server over the complete catalog, and `summary.breakdown` counts the templates per " +
        "`recurring` value and per location zone. Answer any 'how many' question from `summary` — it is " +
        "authoritative, and counting the rows yourself gets it wrong. Each row carries " +
        "`prepaidpackagetemplateid`, `prepaidpackagetemplatename`, `databyte` (bytes), `cost`, " +
        "`perioddays`, `locationzoneid`, `rdbLocationZones.locationzonename` and `recurring`. " +
        "Do NOT use this to list packages assigned to a specific subscriber — use `list_subscriber_packages`.",
      // No `accountId` — see the matching note in apps/mcp-server/src/tools.ts.
      // OCS rejected every call that carried it, and it is not a rename of any
      // of the five properties OCS does accept.
      inputSchema: {},
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_package_templates",
      "listPrepaidPackageTemplate",
      TOOL_SCOPES["list_package_templates"]!,
      ctx,
      async (_args, token) =>
        ocsCall(ctx.env, token, "listPrepaidPackageTemplate", {}, "templates"),
    ),
  );

  server.registerTool(
    "create_package_template",
    {
      title: "Create Package Template",
      description:
        "Use this to create a new prepaid package template in the product catalog. Templates define " +
        "allowances, cost, location zones, validity, and throttling thresholds that are reused each " +
        "time the template is assigned to a subscriber. " +
        "Params: `template` (full template configuration as a JSON string). OCS accepts " +
        "`prepaidpackagetemplatename`, `databyte`, `perioddays`, `locationzoneid`, `cost`, `recurring`, " +
        "and `throttlingActive`. `name` maps to prepaidpackagetemplatename, `dataLimit` maps to databyte, " +
        "`validityDays` maps to perioddays, and `locationZoneId` maps to locationzoneid. There is no `price` " +
        "field and no `description` field. " +
        "NEVER express an unlimited plan as a zero data allowance: OCS reads 0 as a literal zero, and " +
        "because `throttlingThreshold1Perc` is a percentage OF THE BUNDLE, a zero bundle leaves the " +
        "throttle trigger uncomputable and every storefront renders \"0 MB\". " +
        "Describe the plan as it is sold and the OCS fields are derived for you: `unlimited: true`, " +
        "`throttleAfterMb` (the full-speed window, e.g. 500 for \"500 MB/day\"), `throttleKbps` (the " +
        "speed AFTER that window) and `perioddays` (1 for a daily bucket). The bundle ceiling is sized " +
        "so the window is a whole trigger percentage and the bundle cannot be drained even at the " +
        "throttled rate for the whole period, because a plan sold as unlimited must not run out. " +
        "`throttleKbps` is a SPEED and must be an OCS bitrate (128, 256, 384, 512, 1024, 3072, 5120, " +
        "7680, 10240, 20480, 51200, 102400); passing megabytes here is rejected. " +
        "Returns: created template record with the new `templateId`. " +
        "Do NOT use this to modify an existing template — use `modify_template_core`. " +
        "After creation, call `list_package_templates` to confirm the template is visible.",
      inputSchema: {
        template: z
          .string()
          .describe("Full template configuration as JSON string"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "create_package_template",
      "createPrepaidPackageTemplate",
      TOOL_SCOPES["create_package_template"]!,
      ctx,
      async ({ template }, token) => {
        const params = packageTemplateCreateFromToolJson(template);
        if ("error" in params) {
          return {
            isError: true,
            content: [{ type: "text" as const, text: `Error: ${params.error}` }],
          };
        }
        return ocsCall(ctx.env, token, "createPrepaidPackageTemplate", params);
      },
    ),
  );

  server.registerTool(
    "modify_template_core",
    {
      title: "Modify Template Core Settings",
      description:
        "Use this to change the core fields of an existing package template: name, data/voice/SMS limits, " +
        "pricing, validity period, and location zone. Changes affect future package assignments from this " +
        "template but do NOT retroactively change packages already assigned to subscribers. " +
        "Params: `templateId` (integer from `list_package_templates`), `changes` (JSON string with fields " +
        "to modify, e.g. {\"name\": \"Europe 5GB\", \"dataLimit\": 5368709120}). " +
        "`name` maps to prepaidpackagetemplatename and `dataLimit` maps to databyte. " +
        "A template id inside `changes` is ignored so the `templateId` argument wins. " +
        "There is no `price` field and no `description` field. " +
        "Returns: updated template record. " +
        "Do NOT use this to modify throttling thresholds — use `modify_template_throttling`. " +
        "Do NOT use this to modify recurring/renewal settings — use `modify_template_recurring`.",
      inputSchema: {
        templateId: z.number().describe("The template ID"),
        changes: z.string().describe("Core fields to modify as JSON string"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_template_core",
      "modifyPPTCore",
      TOOL_SCOPES["modify_template_core"]!,
      ctx,
      async ({ templateId, changes }, token) => {
        const mapped = packageTemplateModifyFromToolJson(changes, "modifyPPTCore");
        if ("error" in mapped) {
          return {
            isError: true,
            content: [{ type: "text" as const, text: `Error: ${mapped.error}` }],
          };
        }
        // A spread into an object literal makes param-drift read the spread
        // name as a wire key. The path id is set on the mapped object instead.
        const wire: Record<string, unknown> = mapped;
        wire.templateId = templateId;
        return ocsCall(ctx.env, token, "modifyPPTCore", wire);
      },
    ),
  );

  server.registerTool(
    "modify_template_recurring",
    {
      title: "Modify Template Recurring Settings",
      description:
        "Use this to change the auto-renewal configuration of a package template: periodicity " +
        "(daily/weekly/monthly), occurrence count, and renewal trigger conditions. Changes affect " +
        "future assignments and existing recurring packages assigned from this template. " +
        "Params: `templateId` (integer from `list_package_templates`), `changes` (JSON string with " +
        "recurring fields, e.g. {\"periodicity\": \"monthly\", \"occurrences\": 12}). " +
        "`periodicity` maps to recurringPeriodicityType (0 daily, 1 weekly, 2 monthly) and " +
        "`occurrences` maps to nbOccurrence. " +
        "Returns: updated template record with new recurring settings. " +
        "Do NOT use this to stop an individual subscriber's recurring renewal — use `stop_resume_recurring_package`. " +
        "Do NOT use this to change core template fields like data limits — use `modify_template_core`.",
      inputSchema: {
        templateId: z.number().describe("The template ID"),
        changes: z
          .string()
          .describe("Recurring fields to modify as JSON string"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_template_recurring",
      "modifyPPTRecurring",
      TOOL_SCOPES["modify_template_recurring"]!,
      ctx,
      async ({ templateId, changes }, token) => {
        const mapped = packageTemplateModifyFromToolJson(changes, "modifyPPTRecurring");
        if ("error" in mapped) {
          return {
            isError: true,
            content: [{ type: "text" as const, text: `Error: ${mapped.error}` }],
          };
        }
        const wire: Record<string, unknown> = mapped;
        wire.templateId = templateId;
        return ocsCall(ctx.env, token, "modifyPPTRecurring", wire);
      },
    ),
  );

  server.registerTool(
    "modify_template_throttling",
    {
      title: "Modify Template Throttling",
      description:
        "Use this to change the bandwidth throttling thresholds on a package template. " +
        "WARNING: changes apply immediately to ALL existing subscriber packages created from this template, " +
        "not just future ones. Setting a lower threshold will NOT retroactively throttle subscribers " +
        "already below the new threshold (the system does not re-check existing usage). " +
        "Params: `templateId` (integer), `changes` (JSON string with throttling fields, e.g. " +
        "{\"throttlingActive\": true, \"throttlingThreshold1Perc\": 80, \"throttlingThreshold1Limit\": 1024}). " +
        "`firstThresholdPercent` maps to throttlingThreshold1Perc and `firstThresholdLimitKbps` maps to " +
        "throttlingThreshold1Limit. `throttlingErrorAction` is an integer; continue_unthrottled is refused. " +
        "Returns: updated template record with new throttling configuration. " +
        "Do NOT use this to throttle a single subscriber — use `hlr_set_bitrate` instead. " +
        "Do NOT use this to change core package limits — use `modify_template_core`.",
      inputSchema: {
        templateId: z.number().describe("The template ID"),
        changes: z
          .string()
          .describe("Throttling fields to modify as JSON string"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "modify_template_throttling",
      "modifyPPTThrottling",
      TOOL_SCOPES["modify_template_throttling"]!,
      ctx,
      async ({ templateId, changes }, token) => {
        const mapped = packageTemplateModifyFromToolJson(changes, "modifyPPTThrottling");
        if ("error" in mapped) {
          return {
            isError: true,
            content: [{ type: "text" as const, text: `Error: ${mapped.error}` }],
          };
        }
        const wire: Record<string, unknown> = mapped;
        wire.templateId = templateId;
        return ocsCall(ctx.env, token, "modifyPPTThrottling", wire);
      },
    ),
  );

  server.registerTool(
    "list_location_zones",
    {
      title: "List Location Zone Elements",
      description:
        "Use this to list countries and networks within a specific location zone. " +
        "WARNING: this method has a known Jackson deserialization bug in the upstream OCS API that " +
        "may return malformed responses. Prefer `list_detailed_location_zones` for reliable results. " +
        "Params: `locationZoneId` (integer, REQUIRED — OCS takes a bare zone id, not an object; " +
        "there is no unfiltered call). " +
        "Returns: array of zone element records with country and operator entries. " +
        "Do NOT use this for reliable zone data — use `list_detailed_location_zones` instead. " +
        "Do NOT use this to create zones — use `create_location_zone`.",
      inputSchema: {
        locationZoneId: z.number().describe("Zone ID (required — OCS has no unfiltered call for this method)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_location_zones",
      "listLocationZoneElement",
      TOOL_SCOPES["list_location_zones"]!,
      ctx,
      async ({ locationZoneId }, token) => {
        // OCS takes a bare integer zone id; there is no `{}`-shaped
        // unfiltered call. Schema now enforces this is present.
        return ocsCall(ctx.env, token, "listLocationZoneElement", locationZoneId);
      },
    ),
  );

  // Fix #15: OCS expects bare integer (resellerId), not {}
  // #663 (payload half): 325 KB on this tenant — the largest MCP result proven to
  // work, i.e. sitting on the edge. Project and cap so a zone-heavy tenant cannot
  // reproduce get_tariff's silent failure.
  server.registerTool(
    "list_detailed_location_zones",
    {
      title: "List Detailed Location Zones",
      description:
        "Use this as the preferred way to list location zones with full detail: included countries, " +
        "operator networks, zone IDs, and names. This is the working alternative to `list_location_zones` " +
        "which has a known upstream deserialization bug. Use `locationZoneId` values from this response " +
        "when creating or editing package templates. " +
        "Params: `resellerId` (integer, optional — omit to use the token owner's reseller); " +
        "`verbose` (optional boolean, default false — returns the full untouched zone shape with a " +
        "smaller operator-entry budget). " +
        "Returns BY DEFAULT a projected result: `totalZones`, `returnedZones`, `totalOperators`, " +
        "`returnedOperators`, `truncated`, a `fields` legend, and `zones[]` each with `zoneId`, " +
        "`zoneName`, `operatorCount` and short-keyed `operators[]` (`net`, `iso`, `op`). Per-operator " +
        "mccMncs, tadigs, continent, countryCode, countryName, utcOffset and the repeated reseller " +
        "object are dropped unless `verbose` is true. " +
        `Operator entries are capped at ${ZONES_MAX_OPERATORS} so the result fits the MCP transport ` +
        "(issue #663); when the cap bites, `truncated` is true and `note` says so — never silently. " +
        "Do NOT use `list_location_zones` when you need reliable data — always use this tool instead.",
      inputSchema: {
        resellerId: z
          .number()
          .optional()
          .describe("Reseller ID (omit to use token owner's reseller)"),
        verbose: z
          .boolean()
          .optional()
          .describe(
            "Return the full untouched OCS zone shape instead of the projected zones (smaller operator budget)",
          ),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_detailed_location_zones",
      "listDetailedLocationZone",
      TOOL_SCOPES["list_detailed_location_zones"]!,
      ctx,
      async ({ resellerId, verbose }, token) => {
        const id = resellerId ?? (await getDefaultResellerId(ctx.env, token));
        const raw = await new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token).call<unknown>(
          "listDetailedLocationZone",
          id,
        );
        const bounded = boundZonesResult(raw, { resellerId: id, verbose });
        // Compact serialisation — see the note on get_tariff.
        return {
          content: [{ type: "text", text: JSON.stringify(bounded) }],
        };
      },
    ),
  );

  server.registerTool(
    "list_destination_prefixes",
    {
      title: "List Destination List Prefixes",
      description:
        "Use this to list the phone number prefixes (country dialling codes) within a specific " +
        "named destination list. Destination lists control which countries a subscriber may call on " +
        "voice/SMS packages. You must already know the `destinationListId` to use this tool. " +
        "Params: `destinationListId` (integer, REQUIRED — OCS takes a bare list id, not an object; " +
        "there is no unfiltered call). " +
        "Returns: array of prefix records with country code and E.164 prefix. " +
        "Do NOT use this to discover the destination list catalog — use `list_destination_lists` for that. " +
        "For data-only eSIM products without MOC voice, destination lists are irrelevant.",
      inputSchema: {
        destinationListId: z
          .number()
          .describe("Destination list ID (required — OCS has no unfiltered call for this method)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_destination_prefixes",
      "listDestinationListPrefix",
      TOOL_SCOPES["list_destination_prefixes"]!,
      ctx,
      async ({ destinationListId }, token) => {
        return ocsCall(ctx.env, token, "listDestinationListPrefix", destinationListId);
      },
    ),
  );

  server.registerTool(
    "create_location_zone",
    {
      title: "Create Location Zone",
      description:
        "Use this to create a new location zone — a named collection of countries and operators " +
        "that defines where a package can be used. Location zones are required when creating package " +
        "templates. OCS accepts locationZoneName, tadigList (TADIG codes), and networkProfileId. " +
        "Params: `zone` (JSON string). `name` is an alias of locationZoneName and `tadigs` is an alias " +
        "of tadigList. ISO country codes and MCC-MNC operator ids are not TADIG codes and are refused. " +
        "Use `list_network_profiles` for a networkProfileId. " +
        "Returns: created zone record with the new `locationZoneId`. " +
        "Do NOT use this to modify an existing zone — no edit tool exists yet (gap G-19, pending eSIMVault). " +
        "After creation, verify with `list_detailed_location_zones`.",
      inputSchema: {
        zone: z.string().describe("Zone configuration as JSON string"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "create_location_zone",
      "createLocationZone",
      TOOL_SCOPES["create_location_zone"]!,
      ctx,
      async ({ zone }, token) => {
        const params = locationZoneCreateParams(zone);
        if ("error" in params) {
          return {
            isError: true,
            content: [{ type: "text" as const, text: `Error: ${params.error}` }],
          };
        }
        return ocsCall(ctx.env, token, "createLocationZone", params);
      },
    ),
  );

  // =========================================================================
  // 5. STATISTICS TOOLS
  // =========================================================================

  // Fix #4: OCS expects { subscriber: { iccid }, period: { start, end } }
  // not { iccid, startDate, endDate }
  server.registerTool(
    "subscriber_usage",
    {
      title: "Subscriber Usage Over Period",
      description:
        "Use this to retrieve daily data, voice, and SMS usage for a subscriber over a date range. " +
        "Hard limit: maximum 7 days per query — do not exceed or OCS will return an error. " +
        "Params: `iccid` (subscriber identifier), `startDate` (YYYY-MM-DD, inclusive), " +
        "`endDate` (YYYY-MM-DD, inclusive, max 7 days from start). " +
        "Returns: array of daily usage records. Each record contains a `usageType` integer code: " +
        "1=MOC (mobile-originated call), 15=MTC (mobile-terminated call), " +
        "21=MO-SMS (outbound SMS), 22=MT-SMS (inbound SMS), " +
        "33=Data, 40=MOC VoIP, 41=MTC VoIP. " +
        "Do NOT use this for event-level network activity — use `subscriber_network_events` for attach/detach events. " +
        "Do NOT use this to check current package allowances — use `list_subscriber_packages`.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        startDate: z.string().describe("Start date (YYYY-MM-DD, inclusive)"),
        endDate: z
          .string()
          .describe(
            "End date (YYYY-MM-DD, inclusive, max 7 days from start)",
          ),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "subscriber_usage",
      "subscriberUsageOverPeriod",
      TOOL_SCOPES["subscriber_usage"]!,
      ctx,
      async ({ iccid, startDate, endDate }, token) =>
        ocsCall(ctx.env, token, "subscriberUsageOverPeriod", usageOverPeriodParams(iccid, startDate, endDate)),
    ),
  );

  // Fix #5: same nested shape as subscriberUsageOverPeriod
  server.registerTool(
    "subscriber_network_events",
    {
      title: "Subscriber Network Events",
      description:
        "Use this to retrieve timestamped network events for a subscriber: attach, detach, location " +
        "updates, and handovers between operators. Useful for connectivity troubleshooting, roaming " +
        "activity verification, and fraud pattern detection. Max 7 days per query. " +
        "Params: `iccid` (subscriber identifier), `startDate` (YYYY-MM-DD, inclusive), " +
        "`endDate` (YYYY-MM-DD, inclusive, max 7 days from start). " +
        "Returns: array of event records with `timestamp`, `eventType`, `country`, `operator`, `mccMnc`. " +
        "Do NOT use this for daily usage volumes — use `subscriber_usage` for data/voice/SMS byte counts. " +
        "For real-time events (last 24h), prefer `list_recent_ocs_events` which reads from the ring buffer.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID"),
        startDate: z.string().describe("Start date (YYYY-MM-DD, inclusive)"),
        endDate: z.string().describe("End date (YYYY-MM-DD, inclusive)"),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "subscriber_network_events",
      "subscriberNetworkEventsOverPeriod",
      TOOL_SCOPES["subscriber_network_events"]!,
      ctx,
      async ({ iccid, startDate, endDate }, token) =>
        ocsCall(ctx.env, token, "subscriberNetworkEventsOverPeriod", networkEventsOverPeriodParams(iccid, startDate, endDate)),
    ),
  );

  server.registerTool(
    "subscriber_active_period",
    {
      title: "Get Subscriber Active Period",
      description:
        "Use this to retrieve the lifetime activity window for a subscriber: the date of first usage " +
        "and the date of last usage. Useful for churn analysis, dormancy detection, and subscriber " +
        "lifetime value calculations. " +
        "Params: `iccid` (subscriber identifier). " +
        "Returns: object with `firstUseDate` and `lastUseDate` (ISO 8601 strings). " +
        "Do NOT use this to check current package status — use `list_subscriber_packages`. " +
        "Do NOT use this for detailed daily usage patterns — use `subscriber_usage`.",
      inputSchema: { iccid: z.string().describe("The subscriber ICCID") },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "subscriber_active_period",
      "getSubscriberActivePeriod",
      TOOL_SCOPES["subscriber_active_period"]!,
      ctx,
      async ({ iccid }, token) =>
        ocsCall(ctx.env, token, "getSubscriberActivePeriod", subscriberIdParams(iccid)),
    ),
  );

  // =========================================================================
  // 6. MISC TOOLS (tariff, SMS, network profiles)
  // =========================================================================

  // Fix #14: OCS expects bare integer (resellerId); response key is listTariffRule.
  // #663 (payload half): the raw table is ~2.0 MB / 2,657 rules and never traverses
  // the MCP transport. Project, filter server-side, and cap with a loud note.
  server.registerTool(
    "get_tariff",
    {
      title: "Get Customer Tariff",
      description:
        "Use this to retrieve the tariff table for a reseller: per-country, per-operator wholesale " +
        "data/voice/SMS rates. Useful for cost analysis, margin calculations, and identifying " +
        "expensive roaming countries before steering decisions. " +
        "Params: `resellerId` (integer, optional — omit to use the token owner's reseller); " +
        "`country` (optional, ISO-3166 alpha-2 such as \"nl\" OR a full country name such as " +
        "\"Netherlands\", case-insensitive); `trafficType` (optional, \"data\" | \"voice\" | \"sms\" — " +
        "keeps only rules whose corresponding rate is greater than zero); " +
        "`verbose` (optional boolean, default false — returns the full untouched OCS rule shape, " +
        `capped at ${TARIFF_VERBOSE_MAX_RULES} rows, so combine it with the filters). ` +
        "Returns BY DEFAULT a projected result: `totalRules`, `matchedRules`, `returnedRules`, " +
        "`truncated`, a hoisted `currency`, a `fields` legend and `rules[]` of short-keyed rows " +
        "(`iso`, `op`, `data`, `moCall`, `mtCall`, `moSms`, `mtSms`, `active`). Zero rates and " +
        "inactive flags are omitted per row. Nested operator detail (mccMncs, tadigs, continent, " +
        "countryCode, utcOffset), the sponsor object, plan ids and the validity/discount flags are " +
        "dropped unless `verbose` is true. " +
        `Rows are capped at ${TARIFF_MAX_RULES}; when the cap bites, \`truncated\` is true and \`note\` ` +
        "says so explicitly and names the filter params — the result is never silently shortened. " +
        "Filtering happens server-side on the complete OCS table, so a filtered call sees every " +
        "matching rule. Response key in OCS is `listTariffRule`. " +
        "This is the slowest tool on the platform: the OCS table is ~3.4 MB and takes 3 s in a fast " +
        "window and up to ~2 minutes in a slow one, so the call carries a 180 s budget. Expect to " +
        "wait, and do not call it in a loop. " +
        "Do NOT use this to assign a pricing plan to a subscriber — use `modify_subscriber_mobile_plan`. " +
        "This shows the RESELLER's wholesale cost, not what end-users are charged.",
      inputSchema: {
        resellerId: z
          .number()
          .optional()
          .describe("Reseller ID (omit to use token owner's reseller)"),
        country: z
          .string()
          .optional()
          .describe(
            'Filter by country — ISO-3166 alpha-2 ("nl") or full name ("Netherlands"), case-insensitive',
          ),
        trafficType: z
          .enum(["data", "voice", "sms"])
          .optional()
          .describe(
            'Filter to rules whose rate for this traffic type is greater than zero ("data" | "voice" | "sms")',
          ),
        verbose: z
          .boolean()
          .optional()
          .describe(
            `Return the full untouched OCS rule shape instead of the projected rows (capped at ${TARIFF_VERBOSE_MAX_RULES} rows)`,
          ),
      },
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "get_tariff",
      "getCustomerTariff",
      TOOL_SCOPES["get_tariff"]!,
      ctx,
      async ({ resellerId, country, trafficType, verbose }, token) => {
        const id = resellerId ?? (await getDefaultResellerId(ctx.env, token));
        const raw = await new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token).call<unknown>(
          "getCustomerTariff",
          id,
        );
        const bounded = boundTariffResult(raw, {
          resellerId: id,
          country,
          trafficType: trafficType as TrafficType | undefined,
          verbose,
        });
        // Compact serialisation (no indent): pretty-printing the projected table
        // costs ~60% more bytes (232 KB -> 372 KB on reseller 1170) for zero
        // information, and the whole point of #663 is bytes on the wire.
        return {
          content: [{ type: "text", text: JSON.stringify(bounded) }],
        };
      },
    ),
  );

  // Fix #11: OCS expects { imsi, msisdn, text, senderId? }
  // not { iccid, msisdn, message, sender }
  // Resolve ICCID → IMSI; rename message → text, sender → senderId.
  server.registerTool(
    "send_sms",
    {
      title: "Send MT SMS",
      description:
        "Use this to send a mobile-terminated (MT) SMS to a subscriber. Useful for service notifications, " +
        "package expiry alerts, and support messages sent programmatically from the platform. " +
        "Internally resolves ICCID → IMSI via a getSingleSubscriber lookup before forwarding to OCS. " +
        "Params: `iccid` (subscriber identifier), `msisdn` (E.164 phone number of the subscriber), " +
        "`message` (SMS text content, max 160 chars for single SMS in GSM-7 encoding), " +
        "`sender` (optional sender ID or phone number displayed on the device). " +
        "⚠ Messages containing non-GSM-7 characters (any emoji, é, ñ, Chinese, Arabic, Hebrew, etc.) " +
        "trigger UCS-2 encoding which limits a single SMS to 70 characters instead of 160. " +
        "Plan for multi-part splits accordingly. " +
        "Returns: OCS delivery confirmation. " +
        "Do NOT use this for bulk SMS campaigns — this sends one message per call and is rate-limited. " +
        "Requires admin scope.",
      inputSchema: {
        iccid: z.string().describe("The target subscriber ICCID"),
        msisdn: z.string().describe("The target MSISDN"),
        message: z.string().describe("SMS text content"),
        sender: z.string().optional().describe("Sender ID/number (senderId in OCS)"),
        ...DRY_RUN_FIELD,
      },
      annotations: { destructiveHint: true },
    },
    wrapHandler(
      "send_sms",
      "sendMtSms",
      TOOL_SCOPES["send_sms"]!,
      ctx,
      async ({ iccid, msisdn, message, sender }, token) => {
        const cache = new Map<string, SubscriberRecord>();
        const sub = await resolveSubscriberByIccid(ctx.env, token, iccid, cache);
        const imsi = imsiFromSubscriberRecord(sub);
        if (typeof imsi !== "string" || imsi.length === 0) {
          return {
            isError: true,
            content: [{ type: "text", text: `Error: Could not resolve IMSI for ICCID ${iccid}` }],
          };
        }
        const params: Record<string, unknown> = { imsi, msisdn, text: message };
        if (sender) params.senderId = sender;
        return ocsCall(ctx.env, token, "sendMtSms", params);
      },
    ),
  );

  server.registerTool(
    "list_network_profiles",
    {
      title: "List Network Profiles",
      description:
        "Use this to list all network profiles available to this reseller. A network profile defines " +
        "the roaming configuration and operator partnerships for eSIM provisioning. Use profile IDs " +
        "when creating location zones or configuring steering lists. " +
        "Params: none. " +
        "Returns: array of profile records with `profileId`, `name`, and coverage metadata. " +
        "Do NOT use this to list operator steering configurations — use `list_steering_lists` for that.",
      annotations: { readOnlyHint: true },
    },
    wrapHandler(
      "list_network_profiles",
      "listNetworkProfile",
      TOOL_SCOPES["list_network_profiles"]!,
      ctx,
      async (_args, token) => ocsCall(ctx.env, token, "listNetworkProfile"),
    ),
  );
}
